import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../../api/hooks';
import useDemocracyWs, { useDemocracyWsConnected } from '../../../../../../views/democracy/useDemocracyWs';
import {
    Endpoints, QueryKey, WsTopic, WsEventType, DemocracyStatus, SystemSoundKind
} from '../../../../../../views/democracy/constants';
import {
    showQuestion, hideQuestion, showAnswer, hideAnswer, showRating, hideRating,
    showRoundDescription, hideRoundDescription, selectMedia, startTimer, pauseTimer,
    nextQuestion, selectQuestion, selectRound, nextTeam, selectTeam,
    throwQuestion, applyCorrectAnswer
} from '../../../../../../views/democracy/controlsDispatcher';
import { buildMediaUrl, extractErrorMessage } from '../../../../../../views/democracy/utils';
import TeamsColumn from './TeamsColumn';
import RoundsColumn from './RoundsColumn';
import CenterQuestion from './CenterQuestion';
import QuestionsColumn from './QuestionsColumn';
import ManageSanctions from './ManageSanctions';
import './manage.css';

// Локальные дефолты — играют, если для вида звук не загружен в смену (url пустой).
const LOCAL_SOUNDS = {
    [SystemSoundKind.NO_ANSWER]: '/sounds/no_answer.mp3',
    [SystemSoundKind.NEW_SANCTION]: '/sounds/new_sanction.mp3',
    [SystemSoundKind.PRE_START_TIMER]: '/sounds/pre_start_timer.mp3'
};

function playSystemSound(kind, url) {
    const src = url ? buildMediaUrl(url) : LOCAL_SOUNDS[kind];
    console.info('[demo-sound] SOUND получен', { kind, url, src }); // DEBUG (убрать после диагностики)
    if (!src) {
        console.warn('[demo-sound] нет источника звука для kind=', kind); // DEBUG
        return;
    }
    const audio = new Audio(src);
    audio.volume = 0.4;
    audio.play()
        .then(() => console.info('[demo-sound] play() OK:', src)) // DEBUG
        .catch((e) => console.warn('[demo-sound] play() заблокирован/ошибка:', src, e?.name || e)); // DEBUG
}

// Под-таб «Управление»: live-консоль (статусы — в head-card; здесь доска + санкции).
export default function ManagePanel({ democracy }) {
    const { enqueueSnackbar } = useSnackbar();
    const [session, setSession] = useState(null);

    const { data: full } = useApiQuery(
        QueryKey.democracyFull(democracy.id),
        Endpoints.democracies.get(democracy.id),
        { refetchOnMount: 'always' }
    );

    const sessionEnabled = democracy.status === DemocracyStatus.WAITING_ACTIVE || democracy.status === DemocracyStatus.ACTIVE;
    const { data: sessionInitial } = useApiQuery(
        QueryKey.session(democracy.id),
        Endpoints.sessions.get(democracy.id),
        { refetchOnMount: 'always', enabled: sessionEnabled }
    );
    useEffect(() => { if (sessionInitial) setSession(sessionInitial); }, [sessionInitial]);
    useEffect(() => { if (!sessionEnabled) setSession(null); }, [sessionEnabled]);

    useDemocracyWs(sessionEnabled ? WsTopic.controls(democracy.id) : null, WsEventType.CONTROLS_DATA, (p) => setSession(p));
    useDemocracyWs(sessionEnabled ? WsTopic.controls(democracy.id) : null, WsEventType.ACTION_EXCEPTION,
        (p) => enqueueSnackbar(p?.message || 'Ошибка сервера', { variant: 'error' }));
    useDemocracyWs(sessionEnabled ? WsTopic.sound(democracy.id) : null, WsEventType.SOUND, (p) => playSystemSound(p?.kind, p?.url));
    useDemocracyWs(sessionEnabled ? WsTopic.sound(democracy.id) : null, WsEventType.CUSTOM_SOUND, (p) => {
        if (p?.url) new Audio(buildMediaUrl(p.url)).play().catch(() => {});
    });

    // Баннер «связь потеряна» — с grace-периодом, чтобы не мигать на старте/коротких блипах.
    const wsConnected = useDemocracyWsConnected();
    const [showWsWarn, setShowWsWarn] = useState(false);
    useEffect(() => {
        if (!sessionEnabled || wsConnected) { setShowWsWarn(false); return undefined; }
        const timer = setTimeout(() => setShowWsWarn(true), 2500);
        return () => clearTimeout(timer);
    }, [wsConnected, sessionEnabled]);

    const wrap = (fn) => async () => {
        try { const r = await fn(); if (r?.flags) setSession(r); }
        catch (error) { enqueueSnackbar(extractErrorMessage(error, 'Команда не выполнена'), { variant: 'error' }); }
    };
    const wrapWith = (fn) => async (...args) => {
        try { const r = await fn(...args); if (r?.flags) setSession(r); }
        catch (error) { enqueueSnackbar(extractErrorMessage(error, 'Команда не выполнена'), { variant: 'error' }); }
    };
    // Для toggle-кнопок: тост + проброс ошибки, чтобы ToggleButton сразу снял спиннер (не ждал WS/таймаут).
    const wrapToggle = (fn) => async () => {
        try { const r = await fn(); if (r?.flags) setSession(r); }
        catch (error) { enqueueSnackbar(extractErrorMessage(error, 'Команда не выполнена'), { variant: 'error' }); throw error; }
    };

    const id = democracy.id;
    const dispatch = {
        showQuestion: wrapToggle(() => showQuestion(id)), hideQuestion: wrapToggle(() => hideQuestion(id)),
        showAnswer: wrapToggle(() => showAnswer(id)), hideAnswer: wrapToggle(() => hideAnswer(id)),
        showRating: wrapToggle(() => showRating(id)), hideRating: wrapToggle(() => hideRating(id)),
        showRoundDescription: wrapToggle(() => showRoundDescription(id)), hideRoundDescription: wrapToggle(() => hideRoundDescription(id)),
        startTimer: wrapToggle(() => startTimer(id)), pauseTimer: wrapToggle(() => pauseTimer(id)),
        nextQuestion: wrap(() => nextQuestion(id)), selectQuestion: wrapWith((o) => selectQuestion(id, o)),
        selectRound: wrapWith((o) => selectRound(id, o)), nextTeam: wrap(() => nextTeam(id)),
        selectTeam: wrapWith((o) => selectTeam(id, o)), selectMedia: wrapWith((m) => selectMedia(id, m)),
        throwQuestion: wrap(() => throwQuestion(id)), applyCorrectAnswer: wrap(() => applyCorrectAnswer(id))
    };

    const flags = session?.flags || {};

    return (
        <>
            {showWsWarn && (
                <div className="mg-info mg-warn">Связь с сервером потеряна — переподключение… Команды и live-обновления временно недоступны.</div>
            )}
            {!sessionEnabled ? (
                <div className="mg-info">Управление доступно в статусах «Ожидание» или «Активна» — смените статус в шапке игры.</div>
            ) : !session ? (
                <div className="mg-info">Загрузка сессии…</div>
            ) : (
                <>
                    <div className="ctrl-board">
                        <TeamsColumn session={session} democracy={democracy} dispatch={dispatch} />
                        <div className="ctrl-row">
                            <CenterQuestion session={session} flags={flags} dispatch={dispatch} />
                            <QuestionsColumn rounds={full?.rounds || []} session={session} dispatch={dispatch} />
                        </div>
                        <RoundsColumn rounds={full?.rounds || []} session={session} dispatch={dispatch} />
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <ManageSanctions democracy={democracy} teams={[...(session.teams || [])].sort((a, b) => a.orderId - b.orderId)} />
                    </div>
                </>
            )}
        </>
    );
}

ManagePanel.propTypes = {
    democracy: PropTypes.shape({ id: PropTypes.number.isRequired, status: PropTypes.string }).isRequired
};
